import React from "react";
import { CheckIcon, XMarkIcon } from "@heroicons/react/24/outline";
import {
  validatePassword,
  getPasswordStrengthBarColor,
  PasswordStrength,
  PasswordRequirement,
} from "../utils/passwordValidation";

interface PasswordStrengthIndicatorProps {
  password: string;
  showRequirements?: boolean;
  className?: string;
}

const PasswordStrengthIndicator: React.FC<PasswordStrengthIndicatorProps> = ({
  password,
  showRequirements = true,
  className = "",
}) => {
  const strength = validatePassword(password);

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Strength Bar */}
      <div className="space-y-2">
        <div className="flex justify-between items-center">
          <span className="text-sm font-medium text-gray-700">
            Password Strength
          </span>
          <span className={`text-sm font-medium ${strength.color}`}>
            {strength.label}
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all duration-300 ${getPasswordStrengthBarColor(
              strength.score
            )}`}
            style={{ width: `${(strength.score / 4) * 100}%` }}
          />
        </div>
      </div>

      {/* Requirements List */}
      {showRequirements && password && (
        <div className="space-y-2">
          <span className="text-sm font-medium text-gray-700">
            Password Requirements
          </span>
          <div className="grid grid-cols-1 gap-2">
            {strength.requirements.map((requirement: PasswordRequirement) => {
              const isValid = requirement.test(password);
              return (
                <div
                  key={requirement.id}
                  className="flex items-center space-x-2"
                >
                  <div
                    className={`flex items-center justify-center w-4 h-4 rounded-full ${
                      isValid
                        ? "bg-green-100 text-green-600"
                        : "bg-gray-100 text-gray-400"
                    }`}
                  >
                    {isValid ? (
                      <CheckIcon className="w-3 h-3" />
                    ) : (
                      <XMarkIcon className="w-3 h-3" />
                    )}
                  </div>
                  <span
                    className={`text-sm ${
                      isValid ? "text-green-600" : "text-gray-500"
                    }`}
                  >
                    {requirement.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Security Tips */}
      {password && strength.score < 3 && (
        <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
          <p className="text-sm text-yellow-800">
            <strong>Security Tip:</strong> Use a mix of uppercase, lowercase,
            numbers, and special characters for a stronger password.
          </p>
        </div>
      )}
    </div>
  );
};

export default PasswordStrengthIndicator;
